import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  IconButton,
  Menu,
  TextField,
} from "@mui/material";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import {
  MoreVert as MoreVertIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { useClientInfo, axiosInstance } from "../../utils/commonImports";

import VoucherFormAdd from "./VoucherFormAdd";
import { useConfirmation } from "../../hooks/useConfirmation";
import dayjs from "dayjs";
import MyDatePicker from "../../components/MyDatePicker";

export default function VoucherList() {
  const { clientId } = useClientInfo();
  const tableRef = useRef();

  // Data states
  const [vouchers, setVouchers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [ledgers, setLedgers] = useState([]);

  // Filter states
  const [filterVoucherType, setFilterVoucherType] = useState("");
  const [filterGroup, setFilterGroup] = useState("");
  const [filterLedger, setFilterLedger] = useState("");
  const [filterFromDate, setFilterFromDate] = useState(null);
  const [filterToDate, setFilterToDate] = useState(null);
  const [filterVoucherNumber, setFilterVoucherNumber] = useState("");

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Add/Edit
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);

  // Menu & Snackbar
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    msg: "",
    severity: "success",
  });
  const [confirm, ConfirmationDialogComponent] = useConfirmation();

  // ✅ Fetch groups
  const fetchGroups = useCallback(async () => {
    if (!clientId) return;
    try {
      const res = await axiosInstance.get("/api/vouchers/groups", {
        params: { client_id: clientId },
      });
      // Debug
      console.log("🟢 Groups:", res.data?.length ?? 0);
      setGroups(res.data || []);
    } catch (err) {
      console.error("❌ Groups API:", err.response?.data || err.message);
      setSnackbar({
        open: true,
        msg: "Failed to fetch account groups.",
        severity: "error",
      });
    }
  }, [clientId]);

  // ✅ Fetch ledgers (by group or all)
  const fetchLedgersByGroup = useCallback(
    async (groupId = "") => {
      if (!clientId) return;

      // Build params and only send group_id when present
      const params = { client_id: clientId };
      if (groupId !== "" && groupId !== null && groupId !== undefined) {
        // ensure string to be safe in querystring
        params.group_id = String(groupId);
      }

      try {
        const res = await axiosInstance.get("/api/vouchers/ledgers/by-group", {
          params,
        });
        // Debug
        console.log(
          "🟢 Ledgers for group",
          params.group_id ?? "(all)",
          ":",
          res.data?.length ?? 0,
        );
        setLedgers(res.data || []);
      } catch (err) {
        console.error("❌ Ledgers API:", err.response?.data || err.message);
        setSnackbar({
          open: true,
          msg: "Failed to fetch ledgers.",
          severity: "error",
        });
      }
    },
    [clientId],
  );

  // ✅ Fetch vouchers
  const fetchVouchers = useCallback(async () => {
    if (!clientId) return;
    try {
      const params = new URLSearchParams();
      params.append("client_id", clientId);
      if (filterVoucherType) params.append("voucher_type", filterVoucherType);
      if (filterLedger) params.append("ledger_id", filterLedger);
      if (filterFromDate)
        params.append("from_date", dayjs(filterFromDate).format("YYYY-MM-DD"));
      if (filterToDate)
        params.append("to_date", dayjs(filterToDate).format("YYYY-MM-DD"));
      if (filterVoucherNumber)
        params.append("voucher_number", filterVoucherNumber);

      const res = await axiosInstance.get(`/api/vouchers?${params.toString()}`);
      setVouchers(res.data || []);
    } catch (err) {
      console.error("❌ Vouchers API:", err.response?.data || err.message);
      setSnackbar({
        open: true,
        msg: "Failed to fetch vouchers.",
        severity: "error",
      });
    }
  }, [
    clientId,
    filterVoucherType,
    filterLedger,
    filterVoucherNumber,
    filterFromDate,
    filterToDate,
  ]);

  useEffect(() => {
    fetchGroups();
    // load all ledgers initially
    fetchLedgersByGroup();
  }, [fetchGroups, fetchLedgersByGroup]);

  useEffect(() => {
    const fetchVouchers = async () => {
      if (!clientId) return;
      try {
        const params = new URLSearchParams();
        params.append("client_id", clientId);
        if (filterVoucherType) params.append("voucher_type", filterVoucherType);
        if (filterLedger) params.append("ledger_id", filterLedger);
        if (filterFromDate)
          params.append(
            "from_date",
            dayjs(filterFromDate).format("YYYY-MM-DD"),
          );
        if (filterToDate)
          params.append("to_date", dayjs(filterToDate).format("YYYY-MM-DD"));
        if (filterVoucherNumber)
          params.append("voucher_number", filterVoucherNumber);

        const res = await axiosInstance.get(
          `/api/vouchers?${params.toString()}`,
        );
        setVouchers(res.data || []);
      } catch (err) {
        console.error("❌ Vouchers API:", err.response?.data || err.message);
        setSnackbar({
          open: true,
          msg: "Failed to fetch vouchers.",
          severity: "error",
        });
      }
    };
    fetchVouchers();
  }, [
    clientId,
    filterVoucherType,
    filterLedger,
    filterVoucherNumber,
    filterFromDate,
    filterToDate,
  ]);

  // Handlers
  const handleChangePage = (_event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleRowMenuOpen = (event, row) => {
    setAnchorEl(event.currentTarget);
    setMenuRow(row);
  };
  const handleRowMenuClose = () => {
    setAnchorEl(null);
    setMenuRow(null);
  };
  const handleDelete = async (row) => {
    handleRowMenuClose();
    if (!(await confirm("Are you sure you want to delete this voucher?")))
      return;
    try {
      await axiosInstance.delete(`/api/vouchers/${row.id}`);
      setSnackbar({
        open: true,
        msg: "Voucher deleted successfully.",
        severity: "success",
      });
      fetchVouchers();
    } catch (error) {
      console.error("❌ Delete failed:", error);
      setSnackbar({
        open: true,
        msg: "Failed to delete voucher.",
        severity: "error",
      });
    }
  };

  const handleEdit = (row) => {
    handleRowMenuClose();
    setEditData(row);
    setShowForm(true);
  };

  const resetFilters = () => {
    setFilterVoucherType("");
    setFilterGroup("");
    setFilterLedger("");
    setFilterVoucherNumber("");
    setFilterFromDate(null);
    setFilterToDate(null);
    // reload all ledgers after clearing group
    fetchLedgersByGroup();
  };

  const pagedRows = vouchers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const FILTER_HEIGHT = 28;
  const FILTER_FONT = "0.72rem";

  return (
    <Box
      sx={{
        width: "100%",
        height: "calc(100vh - 100px)",
        p: 1,
        bgcolor: "#f8fafc",
      }}
    >
      {!showForm && (
        <Box
          sx={{
            display: "flex",
            gap: 1.5, // slightly more symmetric gap
            alignItems: "center", // fixes vertical alignment
            mb: 1,
            flexWrap: "wrap", // keeps responsiveness
          }}
        >
          {" "}
          {/* Voucher Type */}
          <FormControl size="small" sx={{ minWidth: 140, height: 28 }}>
            <InputLabel
              sx={{ fontSize: FILTER_FONT, lineHeight: 1 }}
              shrink // ✅ ensures label stays floated
            >
              Voucher Type
            </InputLabel>

            <Select
              size="small"
              value={filterVoucherType || ""}
              label="Voucher Type"
              onChange={(e) => setFilterVoucherType(e.target.value)}
              displayEmpty
              sx={{
                height: 28,
                fontSize: FILTER_FONT,
                ".MuiSelect-select": { padding: "4px 8px" },
              }}
              MenuProps={{ PaperProps: { sx: { fontSize: FILTER_FONT } } }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Payment">Payment</MenuItem>
              <MenuItem value="Receipt">Receipt</MenuItem>
              <MenuItem value="Journal">Journal</MenuItem>
              <MenuItem value="Contra">Contra</MenuItem>
            </Select>
          </FormControl>
          {/* Voucher Number */}
          <TextField
            variant="outlined"
            label="Voucher Number"
            value={filterVoucherNumber}
            onChange={(e) => setFilterVoucherNumber(e.target.value)}
            size="small"
            sx={{
              minWidth: 130,
              maxWidth: 150,
              height: FILTER_HEIGHT,
              fontSize: FILTER_FONT,
              background: "#fff",
            }}
            InputProps={{ style: { height: FILTER_HEIGHT } }}
            InputLabelProps={{ style: { fontSize: FILTER_FONT } }}
          />
          {/* Group Account */}
          <FormControl size="small" sx={{ minWidth: 180, height: 28 }}>
            <InputLabel sx={{ fontSize: FILTER_FONT, lineHeight: 1 }}>
              Group Account
            </InputLabel>
            <Select
              size="small"
              value={filterGroup}
              label="Group Account"
              onChange={(e) => {
                const selected = e.target.value;
                setFilterGroup(selected);
                // reset ledger when group changes
                setFilterLedger("");
                // pass selected group id; backend log should now show group_id
                fetchLedgersByGroup(selected);
              }}
              sx={{
                height: 28,
                fontSize: FILTER_FONT,
                ".MuiSelect-select": { padding: "4px 8px" },
              }}
            >
              <MenuItem value="">All</MenuItem>
              {groups.map((g) => (
                <MenuItem
                  key={g.id}
                  value={g.id}
                  sx={{ fontSize: FILTER_FONT }}
                >
                  {g.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Ledger Account */}
          <FormControl size="small" sx={{ minWidth: 180, height: 28 }}>
            <InputLabel sx={{ fontSize: FILTER_FONT, lineHeight: 1 }}>
              Ledger Account
            </InputLabel>
            <Select
              size="small"
              value={filterLedger}
              label="Ledger Account"
              onChange={(e) => {
                setFilterLedger(e.target.value);
              }}
              sx={{
                height: 28,
                fontSize: FILTER_FONT,
                ".MuiSelect-select": { padding: "4px 8px" },
              }}
            >
              <MenuItem value="">All</MenuItem>
              {ledgers.map((l) => (
                <MenuItem
                  key={l.id}
                  value={l.id}
                  sx={{ fontSize: FILTER_FONT }}
                >
                  {l.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Dates */}
          <Box sx={{ mt: 3, display: "flex", alignItems: "center", gap: 0.8 }}>
            <MyDatePicker
              label="From Date"
              value={filterFromDate}
              onChange={setFilterFromDate}
              inputStyle={{
                fontSize: FILTER_FONT,
                minHeight: 28,
                height: 28,
                padding: "4px 8px",
              }}
              labelStyle={{
                fontSize: FILTER_FONT,
                lineHeight: 1,
                marginBottom: 0,
                top: -8,
              }}
              containerStyle={{ width: 140, minHeight: 28, height: 28 }}
            />
            <MyDatePicker
              label="To Date"
              value={filterToDate}
              onChange={setFilterToDate}
              inputStyle={{
                fontSize: FILTER_FONT,
                minHeight: 28,
                height: 28,
                padding: "4px 8px",
              }}
              labelStyle={{
                fontSize: FILTER_FONT,
                lineHeight: 1,
                marginBottom: 0,
                top: -8,
              }}
              containerStyle={{ width: 140, minHeight: 28, height: 28 }}
            />
          </Box>
          {/* Buttons */}
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={resetFilters}
            sx={{ fontSize: "0.7rem", height: 28 }}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditData(null);
              setShowForm(true);
            }}
            sx={{ fontSize: FILTER_FONT, height: 28 }}
          >
            Add
          </Button>
        </Box>
      )}

      {/* Table */}
      {!showForm ? (
        <Paper
          elevation={2}
          sx={{ borderRadius: 2, overflowX: "auto" }}
          ref={tableRef}
        >
          <Table size="small" sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontSize: "0.90rem",
                    fontWeight: 600,
                    background: "#f1f5fb",
                  }}
                >
                  Voucher No
                </TableCell>

                <TableCell
                  sx={{
                    fontSize: "0.82rem",
                    fontWeight: 600,
                    background: "#f1f5fb",
                  }}
                >
                  Date
                </TableCell>

                <TableCell
                  sx={{
                    fontSize: "0.82rem",
                    fontWeight: 600,
                    background: "#f1f5fb",
                  }}
                >
                  Type
                </TableCell>

                {/* NEW */}
                <TableCell
                  sx={{
                    fontSize: "0.82rem",
                    fontWeight: 600,
                    background: "#f1f5fb",
                  }}
                >
                  Transaction Type
                </TableCell>

                <TableCell
                  sx={{
                    fontSize: "0.82rem",
                    fontWeight: 600,
                    background: "#f1f5fb",
                  }}
                >
                  Ledger Account
                </TableCell>

                {/* NEW */}
                <TableCell
                  sx={{
                    fontSize: "0.82rem",
                    fontWeight: 600,
                    background: "#f1f5fb",
                  }}
                >
                  Invoice / Purchase No
                </TableCell>

                <TableCell
                  sx={{
                    fontSize: "0.82rem",
                    fontWeight: 600,
                    textAlign: "right",
                    background: "#f1f5fb",
                  }}
                >
                  Amount
                </TableCell>

                <TableCell
                  sx={{
                    fontSize: "0.82rem",
                    fontWeight: 600,
                    textAlign: "center",
                    background: "#f1f5fb",
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {pagedRows.length > 0 ? (
                pagedRows.map((row) => (
                  <TableRow key={row.id} hover>
                    {/* Voucher No */}
                    <TableCell sx={{ fontSize: "0.75rem" }}>
                      {row.voucher_number || `VCHR-${row.id}`}
                    </TableCell>

                    {/* Date */}
                    <TableCell sx={{ fontSize: "0.75rem" }}>
                      {dayjs(row.voucher_date).format("DD/MM/YYYY")}
                    </TableCell>

                    {/* Voucher Type */}
                    <TableCell sx={{ fontSize: "0.75rem" }}>
                      {row.voucher_type || "-"}
                    </TableCell>

                    {/* Transaction Type */}
                    <TableCell sx={{ fontSize: "0.75rem" }}>
                      {row.transaction_type || "-"}
                    </TableCell>

                    {/* Ledger Account */}
                    <TableCell sx={{ fontSize: "0.75rem" }}>
                      {row.ledger_account_name || "-"}
                    </TableCell>

                    {/* Invoice / Purchase No */}
                    <TableCell sx={{ fontSize: "0.75rem" }}>
                      {row.invoice_number
                        ? row.invoice_number
                        : row.purchase_number
                          ? row.purchase_number
                          : "-"}
                    </TableCell>

                    {/* Amount */}
                    <TableCell
                      sx={{
                        fontSize: "0.75rem",
                        textAlign: "right",
                      }}
                    >
                      {isNaN(Number(row.total_amount))
                        ? "0.00"
                        : Number(row.total_amount).toFixed(2)}
                    </TableCell>

                    {/* Actions */}
                    <TableCell sx={{ textAlign: "center" }}>
                      <IconButton
                        size="small"
                        onClick={(e) => handleRowMenuOpen(e, row)}
                      >
                        <MoreVertIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No vouchers found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={vouchers.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      ) : (
        <VoucherFormAdd
          editData={editData}
          onCancel={() => setShowForm(false)}
          onSuccess={() => {
            setShowForm(false);
            setSnackbar({
              open: true,
              msg: "Voucher saved",
              severity: "success",
            });
            fetchVouchers();
          }}
        />
      )}

      {/* Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleRowMenuClose}
      >
        <MenuItem onClick={() => handleEdit(menuRow)} sx={{ fontSize: 13 }}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem
          onClick={() => handleDelete(menuRow)}
          sx={{ fontSize: 13, color: "error.main" }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.msg}
        </Alert>
      </Snackbar>

      <ConfirmationDialogComponent />
    </Box>
  );
}
